import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  ApiError,
  type Branding,
  type SetupStage,
} from '../lib/api.ts';
import { Mark } from './Mark.tsx';

/**
 * Shared by admin first-login and supervisor invites. They differ only
 * in which endpoint they call, so those are passed in rather than
 * duplicating the whole two-stage flow.
 */
interface Props {
  readonly branding: Branding | null;
  readonly onDone: () => void;
  readonly begin: (password: string) => Promise<SetupStage>;
  readonly confirm: (password: string, code: string) => Promise<SetupStage>;
  readonly heading?: string;
}

/**
 * First login: choose a password and enrol two-factor in one flow.
 *
 * The password is held across both steps because the server only
 * commits it once the TOTP code is confirmed -- enrolling without
 * confirmation would lock the admin out of their own instance.
 */
type Stage = 'password' | 'scan' | 'done';

const MESSAGES: Record<string, string> = {
  setup_token_invalid: 'This setup link is invalid or already used.',
  // The supervisor equivalent, and the one almost everybody arrives
  // through -- it was falling to the generic message, which tells a
  // person to retry a link that will never work again.
  invite_invalid: 'This invitation link has already been used or has '
    + 'expired. Ask your administrator to send a new one.',
  not_found: 'This account is no longer active. Ask your administrator '
    + 'to set one up for you.',
  invalid_code: 'That code is not valid. Try the next one.',
  no_admin_account: 'No administrator account exists for this instance.',
  // The one a real person hits, and it was landing on the
  // generic message, which made a rejected password look like
  // a broken link.
  bad_request: 'That password is too short. Use at least twelve '
    + 'characters.',
};

export const Setup = ({
  branding,
  onDone,
  begin: beginCall,
  confirm: confirmCall,
  heading,
}: Props) => {
  const [stage, setStage] = useState<Stage>('password');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [uri, setUri] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);

  // The otpauth URI carries the shared secret, so it is rendered
  // locally and never sent anywhere else.
  useEffect(() => {
    if (stage === 'scan' && uri && canvas.current) {
      QRCode.toCanvas(canvas.current, uri, { width: 208, margin: 1 });
    }
  }, [stage, uri]);

  const fail = (e: unknown) => {
    const c = e instanceof ApiError ? e.code : 'unknown';
    setError(MESSAGES[c] ?? 'Something went wrong. Please try again.');
  };

  const begin = async () => {
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    // Checked here as well as on the server, so the answer is
    // immediate and specific rather than a round trip that comes
    // back as a generic failure.
    if (password.length < 12) {
      setError('Use at least twelve characters.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await beginCall(password);
      if (result.stage === 'confirm_totp') {
        setUri(result.uri);
        setStage('scan');
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    setError('');
    try {
      await confirmCall(password, code.replace(/\D/g, ''));
      setStage('done');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const name = branding?.identity?.product_name ?? branding?.name ?? '';
  const secret = uri.match(/secret=([A-Z2-7]+)/)?.[1] ?? '';

  return (
    <main className="loginWrap">
      <div className="loginCard">
        <Mark size={46} />
        <h1>{name}</h1>
        <p className="mono">
          {stage === 'password' && (heading ?? 'Set up your account')}
          {stage === 'scan' && 'Add two-factor'}
          {stage === 'done' && 'Ready'}
        </p>

        {error && <p className="err">{error}</p>}

        {stage === 'password' && (
          <>
            <label>
              <span className="mono">New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label>
              <span className="mono">Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && begin()}
              />
            </label>
            <p className="hint">At least 12 characters.</p>
            <button
              className="primary"
              disabled={busy || password.length < 12 || !confirm}
              onClick={begin}
            >
              {busy ? 'Saving...' : 'Continue'}
            </button>
          </>
        )}

        {stage === 'scan' && (
          <>
            <p className="hint">
              Scan this with Google Authenticator, 1Password, or any
              authenticator app.
            </p>
            <canvas ref={canvas} className="qr" />
            <p className="hint">
              Cannot scan? Enter this key manually:
              <br />
              <code>{secret}</code>
            </p>
            <label>
              <span className="mono">6-digit code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                className="otp"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && finish()}
              />
            </label>
            <button
              className="primary"
              disabled={busy || code.replace(/\D/g, '').length !== 6}
              onClick={finish}
            >
              {busy ? 'Verifying...' : 'Finish setup'}
            </button>
          </>
        )}

        {stage === 'done' && (
          <>
            <p className="hint">
              Your account is ready. Keep your authenticator app -- you
              will need it every time you sign in.
            </p>
            <button className="primary" onClick={onDone}>
              Go to sign in
            </button>
          </>
        )}
      </div>
    </main>
  );
};
